/**
 * Format a distance in meters for display.
 * Returns value in meters if < 1 km, otherwise in km with max 2 decimals.
 * @param {number} meters - Distance in meters
 * @param {function} t - i18n translate function
 * @returns {string} Translated string (e.g. "250 m" or "1.5 km")
 */
export const formatDistanceString = (meters, t) => {
  if (meters < 1000) {
    const value = meters.toFixed(1).replace(/\.?0+$/, "");
    return t("risksMap.floodDistance.meters", { value });
  }
  const value = (meters / 1000).toFixed(2).replace(/\.?0+$/, "");
  return t("risksMap.floodDistance.km", { value });
};

export const getWaterFeatureName = (feature) =>
  feature?.properties?.nametext || feature?.properties?.nametxtint || "Unknown";

export const getNearbyWaterFeature = ({
  map,
  point,
  activeRisk,
  riversLayerId,
  lakesLayerId,
  riversProximity = 10,
  disableRivers = false,
}) => {
  if (!map || activeRisk !== "flood") {
    return null;
  }

  const hasLayer = (layerId) => Boolean(layerId && map.getLayer(layerId));

  if (disableRivers === false && hasLayer(riversLayerId)) {
    const bbox = [
      [point.x - riversProximity, point.y - riversProximity],
      [point.x + riversProximity, point.y + riversProximity],
    ];

    const riverFeatures = map.queryRenderedFeatures(bbox, {
      layers: [riversLayerId],
    });

    if (riverFeatures.length > 0) {
      return riverFeatures[0];
    }
  }

  if (!hasLayer(lakesLayerId)) {
    return null;
  }

  const lakeFeatures = map.queryRenderedFeatures(point, {
    layers: [lakesLayerId],
  });

  return lakeFeatures[0] || null;
};

/**
 * Return the first historical-hazard feature rendered under the given screen
 * point, or null. Layer ids that aren't currently on the map are skipped so
 * queryRenderedFeatures never throws on a missing layer.
 */
export const getHistoricalFeatureAtPoint = ({ map, point, layerIds }) => {
  if (!map) {
    return null;
  }

  const layers = (layerIds || []).filter((id) => map.getLayer(id));
  if (layers.length === 0) {
    return null;
  }

  const features = map.queryRenderedFeatures(point, { layers });
  return features[0] || null;
};

/**
 * Return every historical-hazard feature rendered under the given screen point,
 * deduped. A polygon spanning several tiles comes back once per tile, so dedupe
 * by `feature.id` (falling back to the serialized property set when the tileset
 * assigns no ids) — same rationale as useHistoricalFloodEvents.
 */
export const getHistoricalFeaturesAtPoint = ({ map, point, layerId }) => {
  if (!map || !map.getLayer(layerId)) {
    return [];
  }

  const features = map.queryRenderedFeatures(point, { layers: [layerId] });

  const seen = new Set();
  const deduped = [];
  for (const feature of features) {
    const key =
      feature.id !== undefined && feature.id !== null
        ? `id:${feature.id}`
        : `props:${JSON.stringify(feature.properties || {})}`;
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(feature);
    }
  }

  return deduped;
};
